import { useState } from 'react'
import type { RuleSuggestion, RuleSuggestionsResponse } from '../../types/thesis'
import { CONDITION_TYPE_LABELS, PRIORITY_COLORS } from '../../types/thesis'
import { Modal } from '../common/Modal'
import { Button } from '../common/Button'

interface RuleSuggestionsProps {
  isOpen: boolean
  onClose: () => void
  suggestions: RuleSuggestionsResponse | null
  isLoading: boolean
  onCreateRules: (suggestions: RuleSuggestion[]) => void
  isCreating: boolean
}

export function RuleSuggestions({
  isOpen,
  onClose,
  suggestions,
  isLoading,
  onCreateRules,
  isCreating,
}: RuleSuggestionsProps) {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set())

  const toggleSelection = (index: number) => {
    const newSelected = new Set(selectedIndices)
    if (newSelected.has(index)) {
      newSelected.delete(index)
    } else {
      newSelected.add(index)
    }
    setSelectedIndices(newSelected)
  }

  const selectAll = () => {
    if (suggestions?.suggestions) {
      setSelectedIndices(new Set(suggestions.suggestions.map((_, i) => i)))
    }
  }

  const selectNone = () => {
    setSelectedIndices(new Set())
  }

  const handleCreate = () => {
    if (suggestions?.suggestions) {
      const selected = suggestions.suggestions.filter((_, i) => selectedIndices.has(i))
      onCreateRules(selected)
    }
  }

  const formatConditionConfig = (config: Record<string, unknown>): string => {
    const parts: string[] = []
    for (const [key, value] of Object.entries(config)) {
      if (key === 'symbol' || key === 'indicator') {
        parts.push(String(value))
      } else if (key === 'value' || key === 'threshold') {
        parts.push(`${value}`)
      } else if (key === 'value_low') {
        parts.push(`${value}`)
      } else if (key === 'value_high') {
        parts.push(`- ${value}`)
      } else if (key === 'threshold_pct') {
        parts.push(`${value}%`)
      } else if (key === 'numerator') {
        parts.push(`${value}`)
      } else if (key === 'denominator') {
        parts.push(`/ ${value}`)
      }
    }
    return parts.join(' ')
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="AI Rule Suggestions" size="lg">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mb-4" />
          <p className="text-gray-600">Analyzing thesis and generating rule suggestions...</p>
        </div>
      ) : suggestions?.suggestions && suggestions.suggestions.length > 0 ? (
        <>
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Based on <span className="font-medium">{suggestions.thesis_name}</span>, here are
              suggested monitoring rules:
            </p>
            <div className="flex gap-2 mt-2">
              <button
                onClick={selectAll}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={selectNone}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Select none
              </button>
            </div>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {suggestions.suggestions.map((suggestion, index) => (
              <div
                key={index}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedIndices.has(index)
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => toggleSelection(index)}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedIndices.has(index)}
                    onChange={() => toggleSelection(index)}
                    className="mt-1 h-4 w-4 text-primary-600 rounded border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900">{suggestion.name}</span>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${PRIORITY_COLORS[suggestion.priority]}`}
                      >
                        {suggestion.priority}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">
                        {CONDITION_TYPE_LABELS[suggestion.condition_type]}:
                      </span>{' '}
                      {formatConditionConfig(suggestion.condition_config as Record<string, unknown>)}
                    </div>
                    <p className="text-sm text-gray-500 italic">{suggestion.rationale}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={selectedIndices.size === 0}
              isLoading={isCreating}
            >
              Create {selectedIndices.size} Rule{selectedIndices.size !== 1 ? 's' : ''}
            </Button>
          </div>
        </>
      ) : (
        <div className="py-8 text-center">
          <p className="text-gray-600">No rule suggestions available for this thesis.</p>
          <p className="text-sm text-gray-500 mt-2">
            Try adding more detail to the thesis narrative.
          </p>
          <Button variant="secondary" onClick={onClose} className="mt-4">
            Close
          </Button>
        </div>
      )}
    </Modal>
  )
}
